import parsePhoneNumber, { AsYouType, CountryCode, PhoneNumber } from 'libphonenumber-js';
import { Country, getCountry, countries } from './countries';

/**
 * Extracts the national number from an international phone number string.
 * Handles both complete and partial/incomplete numbers.
 */
export function extractNationalNumber(value: string, dialCode: string): string {
  if (!value) return '';

  // First, strip the dial code prefix manually (handles NANP extended codes like +1767)
  if (value.startsWith(`+${dialCode}`)) {
    return value.slice(dialCode.length + 1); // +1 for the '+' sign
  }

  // If value starts with +, try to extract what's after the dial code
  if (value.startsWith('+')) {
    // Remove the + and any leading digits that match a dial code
    return value.replace(/^\+\d{1,4}/, '');
  }

  return value;
}

/**
 * Finds a country by matching its dial code against the start of a phone number.
 */
function findCountryByDialCode(phoneDigits: string): Country | null {
  // Sort by dial code length descending to match longer codes first
  const sortedCountries = [...countries].sort(
    (a, b) => b.dialCode.length - a.dialCode.length,
  );

  return sortedCountries.find(country => phoneDigits.startsWith(country.dialCode)) || null;
}

/**
 * Extracts the country code from a phone number value.
 */
export function extractCountryCode(value: string, defaultCountry: string): string {
  if (!value) {
    return defaultCountry;
  }

  // Strip the + prefix if present
  const phoneDigits = value.startsWith('+') ? value.slice(1) : value;

  // First, try to match by dial code (handles NANP countries like Anguilla +1264)
  const countryByDialCode = findCountryByDialCode(phoneDigits);
  if (countryByDialCode) {
    return countryByDialCode.iso2;
  }

  // Fall back to libphonenumber-js parsing for complete numbers
  let phoneValue = value;
  const ayt = new AsYouType(defaultCountry as CountryCode);

  // Append extra digits to help parsing short values
  if (phoneValue && phoneValue.length <= 4) {
    phoneValue = `${phoneValue}000`;
  }

  ayt.input(phoneValue);
  const phone = ayt.getNumber();

  if (phone) {
    return phone.country || defaultCountry;
  }

  return defaultCountry;
}

/**
 * Converts a national number to international format.
 * Falls back to simple concatenation for partial/incomplete numbers.
 */
export function toInternationalFormat(nationalNumber: string, countryCode: string): string {
  let country: Country;
  if (!nationalNumber) {
    country = getCountry(countryCode)!;
    return `+${country.dialCode}`;
  }

  const phone = parsePhoneNumber(nationalNumber, countryCode as CountryCode);
  if (phone) {
    return phone.number;
  }

  // Fallback for partial/incomplete numbers
  country = getCountry(countryCode)!;
  return `+${country.dialCode}${nationalNumber}`;
}

/**
 * Validates a phone number and returns an error message or true if valid.
 */
export function validatePhoneNumber(
  value: string,
  countryCode: string,
  dialCode: string,
): string|true {
  const phone = parsePhoneNumber(value, {
    defaultCountry: countryCode as CountryCode,
    defaultCallingCode: dialCode,
  });

  if (!phone || !phone.isValid()) {
    return 'Phone number is incomplete or incorrect';
  }

  const phType = phone.getType();
  if (
    phType
    && !['FIXED_LINE_OR_MOBILE', 'FIXED_LINE', 'MOBILE', 'VOIP'].includes(phType)
  ) {
    return 'Personal mobile or landline number required';
  }

  return true;
}

/**
 * Returns true if the phone number (international format) has a valid 10-digit national number.
 */
export function isPhoneValid(value: string, required = false): boolean {
  if (!value || value.trim() === '') {
    return !required;
  }
  const phone = parsePhoneNumber(value);
  if (!phone) {
    return false;
  }
  return phone.nationalNumber.length === 10;
}

/**
 * Parses an international number and returns the parsed phone object.
 * Returns null if parsing fails.
 */
export function parseInternationalNumber(value: string): PhoneNumber|null {
  if (!value || !value.startsWith('+')) {
    return null;
  }

  const phone = parsePhoneNumber(value);
  if (phone && phone.country) {
    return phone;
  }

  return null;
}
